import { useState } from 'react';
import { plural } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Box, Menu, MenuItem, useTheme } from '@mui/material';

import { useRouter } from 'next/router';
import { ErrorBoundary } from 'react-error-boundary';

import { Flags } from '@watershed/constants/flags';
import HelpIcon from '@watershed/icons/components/Help';
import InboxIcon from '@watershed/icons/components/Inbox';
import LightBulbIcon from '@watershed/icons/components/LightBulb';
import SupportNewMessageIcon from '@watershed/icons/components/SupportNewMessage';
import { maybeNotifySentry } from '@watershed/shared-frontend/utils/errorUtils';
import {
  routeForLearningHub,
  routeForSupport,
} from '@watershed/shared-universal/dashboardRoutes';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Button from '@watershed/ui-core/components/Button';
import UnstyledLink from '@watershed/ui-core/components/UnstyledLink';

import { GQSupportCaseIssueType } from '@watershed/app-dashboard/generated/graphql-schema-types';
import { useSupportCaseNotificationCountQuery } from '@watershed/app-dashboard/generated/urql';

import { useFeatureFlag } from '../../utils/FeatureFlag';
import NavPill from '../navigation/NavPill';
import { getSharedSidebarStyles } from '../navigation/variants/sharedSidebarStyles';
import { useSupportCaseDialog } from './NewSupportCaseDialog';
import { OpenDiscussionCount } from './OpenDiscussionCount';

export default function NavHelpButton({
  isOnFinancePage,
  appearsCollapsed,
}: {
  isOnFinancePage: boolean;
  appearsCollapsed: boolean;
}) {
  const router = useRouter();
  const [supportCaseResult] = useSupportCaseNotificationCountQuery({
    variables: {
      issueType: null,
      objectId: null,
    },
  });
  const openDiscussionCount =
    supportCaseResult.data?.supportCaseNotificationCount ?? 0;

  const [helpAnchorEl, setHelpAnchorEl] = useState<null | HTMLButtonElement>(
    null
  );

  const theme = useTheme();
  const isLightTheme = useFeatureFlag(Flags.LightThemeSideNavigation);
  const sharedSidebarStyles = getSharedSidebarStyles(theme, isLightTheme);
  const { COLORS, BOX_SHADOW_FOCUS } = sharedSidebarStyles;
  const openSupportCaseDialog = useSupportCaseDialog();

  return (
    <ErrorBoundary
      fallbackRender={() => null}
      onError={(error) => maybeNotifySentry(error)}
    >
      <Button
        variant="text"
        startIcon={<HelpIcon />}
        aria-controls="help-menu"
        aria-haspopup="true"
        onClick={(e) => setHelpAnchorEl(e.currentTarget)}
        sx={{
          minWidth: 'unset',
          width: '100%',
          height: '32px',
          justifyContent: 'flex-start',
          textAlign: 'left',
          color: COLORS.TEXT_PRIMARY,
          borderRadius: 1,
          paddingInline: 1, // Match alignment of gear icon
          paddingBlock: 1,
          gap: 1,

          '&:hover, &:focus': {
            backgroundColor: COLORS.BACKGROUND_ACTIVE,
          },
          '&:focus-visible': {
            boxShadow: `inset ${BOX_SHADOW_FOCUS}`,
          },
          '&[aria-current="page"]': {
            color: COLORS.TEXT_PRIMARY,
            backgroundColor: COLORS.BACKGROUND_ACTIVE,
          },
          '& .MuiButton-startIcon': {
            margin: 0,
            '& svg': {
              width: 16,
              height: 16,
            },
          },
        }}
        aria-current={helpAnchorEl ? 'page' : undefined}
        data-testid={TestIds.NavHelpButton}
      >
        <Box
          component="span"
          sx={{
            '[data-appears-collapsed="true"] &': {
              display: 'none',
            },
          }}
        >
          <Trans context="Navbar item that opens Learning Hub/Support menu">
            Help
          </Trans>
        </Box>
        {openDiscussionCount ? (
          <NavPill
            label={openDiscussionCount.toString()}
            tooltip={plural(openDiscussionCount, {
              one: `${openDiscussionCount} unread help ticket`,
              other: `${openDiscussionCount} unread help tickets`,
            })}
          />
        ) : null}
      </Button>
      <Menu
        id="help-menu"
        anchorEl={helpAnchorEl}
        keepMounted
        open={Boolean(helpAnchorEl)}
        onClose={() => setHelpAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: sharedSidebarStyles.menuPaperSxProps,
          'data-appears-collapsed': appearsCollapsed,
        }}
        slotProps={{
          list: {
            component: 'div',
          },
        }}
      >
        <MenuItem
          id="new-support-case"
          key="new-support-case"
          component="button"
          sx={{
            width: 1,
            // This little weirdo is holding onto a focus state in a way that
            // just doesn't make sense, and I cannot figure out why.
            // Sometimes you just need a little more CSS violence
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: `${COLORS.BACKGROUND_ACTIVE} !important`,
            },
          }}
          onClick={() => {
            setHelpAnchorEl(null);
            openSupportCaseDialog({
              issueType: GQSupportCaseIssueType.GeneralPlatform,
            });
          }}
          data-testid={TestIds.SupportNewCaseMenuItem}
        >
          <SupportNewMessageIcon />
          <Trans context="Menu item copy to open a support case dialog">
            Ask a question…
          </Trans>
        </MenuItem>

        <MenuItem
          key="support-center"
          component="button"
          sx={{
            width: 1,
          }}
          onClick={() => {
            setHelpAnchorEl(null);
            void router.push(routeForSupport());
          }}
          data-testid={TestIds.SupportCenterMenuItem}
        >
          <InboxIcon />
          <OpenDiscussionCount
            label={
              <Trans context="Navbar item for Support">Support center</Trans>
            }
            count={openDiscussionCount}
            sx={{ display: 'contents' }}
            chipProps={{ sx: { marginLeft: 'auto' } }}
          />
        </MenuItem>

        <UnstyledLink
          href={routeForLearningHub({
            from: 'Sidebar',
          })}
        >
          <MenuItem
            component="a"
            onClick={() => {
              setHelpAnchorEl(null);
            }}
          >
            <LightBulbIcon />
            <Trans context="Navbar item for Learning Hub">Learning hub</Trans>
          </MenuItem>
        </UnstyledLink>
      </Menu>
    </ErrorBoundary>
  );
}
